import type { ReactNode } from 'react';
import { styled } from '@mui/material/styles';

import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';

/**
 * A stop's pin on a route: a round head carrying its number (or a house, or
 * "3·4" for two at one place) over a short tail whose tip is the place. Map my
 * day draws every stop with it, and the callout's map the event and the stops
 * either side of it.
 */

/** The palette's red: a stop, and a leg that cannot be made. */
export const PIN_RED = '#ef3d23';
/** The callout's stops either side of its own event: the same red, darkened. */
export const PIN_DARK = '#8c2f1d';

const Head = styled('span', { shouldForwardProp: (p) => p !== '$hollow' && p !== '$clash' && p !== '$colour' })<{
  $hollow: boolean;
  $clash: boolean;
  $colour: string;
}>(({ $hollow, $clash, $colour }) => ({
  boxSizing: 'border-box',
  minWidth: 20,
  height: 20,
  padding: '0 5px',
  borderRadius: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: FONTS.body,
  fontSize: 11,
  fontWeight: 700,
  lineHeight: 1,
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
  // Hollow: only the neighborhood is known, so the pin does not claim a spot.
  ...($hollow
    ? { background: BRAND.parchment, color: $colour, border: `2px solid ${$colour}` }
    : { background: $colour, color: '#fff' }),
  // Runs at the same time as another stop: ringed, clear of the pin by a hair of white.
  ...($clash && { boxShadow: `0 0 0 2px #fff, 0 0 0 4px ${PIN_RED}` }),
}));

const Tail = styled('span')({
  width: 0,
  height: 0,
  marginTop: -1,
  borderLeft: '5px solid transparent',
  borderRight: '5px solid transparent',
  borderTopWidth: 7,
  borderTopStyle: 'solid',
});

export function PinMark({
  label,
  hollow = false,
  clash = false,
  colour = PIN_RED,
}: {
  label: ReactNode;
  hollow?: boolean;
  clash?: boolean;
  colour?: string;
}) {
  return (
    <>
      <Head $hollow={hollow} $clash={clash} $colour={colour}>
        {label}
      </Head>
      <Tail style={{ borderTopColor: colour }} />
    </>
  );
}

export function House() {
  return (
    <svg width={11} height={11} viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="M12 2.5 1.5 12H5v9.5h5.5v-6h3v6H19V12h3.5z" />
    </svg>
  );
}
